import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, UserPlus, Users, Tv, ChevronRight, Check, X,
  Search, Mail, MessageSquare, Activity, Calendar, Star, ExternalLink,
  Play, Clock, Zap, Heart, Shuffle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_META = {
  netflix:        { name: 'Netflix',       color: '#E50914' },
  hulu:           { name: 'Hulu',          color: '#1CE783' },
  disney_plus:    { name: 'Disney+',       color: '#113CCF' },
  hbo_max:        { name: 'HBO Max',       color: '#B535F6' },
  amazon_prime:   { name: 'Amazon Prime',  color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',     color: '#aaaaaa' },
  peacock:        { name: 'Peacock',       color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',    color: '#0064FF' },
};

function StarRating({ value, onChange, size = 'md' }) {
  const [hovered, setHovered] = useState(0);
  const sz = size === 'sm' ? 'w-4 h-4' : 'w-6 h-6';
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} onClick={() => onChange && onChange(n)}
          onMouseEnter={() => onChange && setHovered(n)}
          onMouseLeave={() => onChange && setHovered(0)}
          className="transition-transform active:scale-90" disabled={!onChange}>
          <Star className={`${sz} transition-colors ${n <= (hovered || value) ? 'fill-yellow-400 text-yellow-400' : 'text-neutral-600'}`} />
        </button>
      ))}
    </div>
  );
}

function TimeAgo({ isoString }) {
  if (!isoString) return null;
  try {
    const delta = (Date.now() - new Date(isoString).getTime()) / 1000;
    if (delta < 60) return <span className="text-xs text-neutral-600">just now</span>;
    if (delta < 3600) return <span className="text-xs text-neutral-600">{Math.floor(delta / 60)}m ago</span>;
    if (delta < 86400) return <span className="text-xs text-neutral-600">{Math.floor(delta / 3600)}h ago</span>;
    return <span className="text-xs text-neutral-600">{Math.floor(delta / 86400)}d ago</span>;
  } catch { return null; }
}

// ── Swipe Match ───────────────────────────────────────────────────────────────

function SwipeMatch({ qmateId, qmateName, getToken }) {
  const [shows, setShows] = useState([]);
  const [index, setIndex] = useState(0);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(false);
  const [done, setDone] = useState(false);
  const [dragX, setDragX] = useState(0);
  const dragStart = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    loadShows();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qmateId]);

  const loadShows = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      // Load pending swipes for this qmate (shows not yet voted on)
      const res = await axios.get(`${API}/swipe/queue/${qmateId}`, { headers });
      setShows(res.data.shows || []);
      setMatches(res.data.matches || []);
      setIndex(0);
      setDone(false);
    } catch (err) {
      // Fallback: load from watchlist + trending
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [myShows, trending] = await Promise.all([
          axios.get(`${API}/shows`, { headers }),
          axios.get(`${API}/tmdb/trending`, { headers }).catch(() => ({ data: { results: [] } })),
        ]);
        const watchlist = (myShows.data || []).filter(s => s.status !== 'watched');
        const trendingShows = (trending.data?.results || []).slice(0, 20).map(s => ({
          show_id: `tmdb_${s.tmdb_id}`,
          title: s.title,
          image_url: s.poster_url,
          service_id: null,
          from_trending: true,
        }));
        // Combine, dedupe by title
        const seen = new Set();
        const combined = [...watchlist, ...trendingShows].filter(s => {
          if (seen.has(s.title)) return false;
          seen.add(s.title);
          return true;
        });
        setShows(combined.slice(0, 30));
        setDone(false);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  const vote = async (liked) => {
    if (swiping || index >= shows.length) return;
    const show = shows[index];
    setSwiping(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${API}/swipe/vote`, {
        qmate_id: qmateId,
        show_id: show.show_id || show.tmdb_id,
        show_title: show.title,
        show_image: show.image_url || show.poster_url,
        liked,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.matched) {
        setMatches(prev => [...prev, { ...show, matched_at: new Date().toISOString() }]);
        toast.success(`🎉 You both want to watch ${show.title}!`);
      }
    } catch {
      // Optimistic — still advance
    }
    setDragX(liked ? 300 : -300);
    setTimeout(() => {
      setDragX(0);
      setIndex(i => i + 1);
      if (index + 1 >= shows.length) setDone(true);
      setSwiping(false);
    }, 300);
  };

  // Touch/drag handlers for swipe gesture
  const onTouchStart = (e) => { dragStart.current = e.touches[0].clientX; };
  const onTouchMove = (e) => {
    if (dragStart.current === null) return;
    setDragX(e.touches[0].clientX - dragStart.current);
  };
  const onTouchEnd = () => {
    if (Math.abs(dragX) > 80) { vote(dragX > 0); }
    else { setDragX(0); }
    dragStart.current = null;
  };
  const onMouseDown = (e) => { dragStart.current = e.clientX; };
  const onMouseMove = (e) => {
    if (dragStart.current === null) return;
    setDragX(e.clientX - dragStart.current);
  };
  const onMouseUp = () => {
    if (Math.abs(dragX) > 80) { vote(dragX > 0); }
    else { setDragX(0); }
    dragStart.current = null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 gap-2 text-neutral-500">
        <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        Loading shows...
      </div>
    );
  }

  const currentShow = shows[index];
  const remaining = shows.length - index;
  const swipeAngle = dragX * 0.08;
  const isLiking = dragX > 40;
  const isNoping = dragX < -40;

  return (
    <div className="space-y-5">
      {/* Matches */}
      {matches.length > 0 && (
        <div className="p-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 space-y-3">
          <p className="text-xs font-black tracking-widest text-emerald-400 uppercase">
            🎉 You Both Want to Watch
          </p>
          <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {matches.map((match, i) => (
              <div key={i} style={{ flexShrink: 0, width: 90 }} className="space-y-1">
                {match.image_url || match.show_image ? (
                  <img src={match.image_url || match.show_image} alt={match.title || match.show_title}
                    className="w-full rounded-xl object-cover" style={{ height: 120 }} />
                ) : (
                  <div className="w-full rounded-xl bg-white/5 flex items-center justify-center" style={{ height: 120 }}>
                    <Tv className="w-6 h-6 text-neutral-600" />
                  </div>
                )}
                <p className="text-xs text-white font-semibold text-center truncate">
                  {match.title || match.show_title}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Card stack */}
      {done || !currentShow ? (
        <div className="text-center py-16 space-y-4">
          <div className="text-5xl">👀</div>
          <p className="text-white font-bold text-lg">All caught up!</p>
          <p className="text-neutral-500 text-sm">
            {matches.length > 0
              ? `You matched on ${matches.length} show${matches.length !== 1 ? 's' : ''}. Time to watch!`
              : `No matches yet — ${qmateName} still needs to swipe.`
            }
          </p>
          <button onClick={loadShows}
            className="flex items-center gap-2 mx-auto px-4 py-2 rounded-full text-sm font-semibold text-neutral-400 border border-white/10 hover:text-white transition-colors">
            <Shuffle className="w-4 h-4" /> Load more shows
          </button>
        </div>
      ) : (
        <div className="relative select-none">
          {/* Progress */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-neutral-500">{remaining} left to swipe</p>
            <p className="text-xs text-neutral-500">with {qmateName}</p>
          </div>

          {/* Card */}
          <div
            ref={cardRef}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            style={{
              transform: `translateX(${dragX}px) rotate(${swipeAngle}deg)`,
              transition: swiping ? 'transform 0.3s ease' : dragX !== 0 ? 'none' : 'transform 0.2s ease',
              cursor: 'grab',
              userSelect: 'none',
              position: 'relative',
            }}
            className="rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0a]"
            style={{
              transform: `translateX(${dragX}px) rotate(${swipeAngle}deg)`,
              transition: swiping ? 'transform 0.3s ease' : dragX !== 0 ? 'none' : 'transform 0.1s ease',
              cursor: 'grab',
              userSelect: 'none',
              position: 'relative',
              borderRadius: 20,
              overflow: 'hidden',
              border: isLiking ? '2px solid rgba(34,197,94,0.5)' : isNoping ? '2px solid rgba(239,68,68,0.5)' : '1px solid rgba(255,255,255,0.1)',
              background: '#0a0a0a',
            }}
          >
            {/* Poster */}
            {(currentShow.image_url || currentShow.poster_url) ? (
              <div style={{ position: 'relative', height: 320 }}>
                <img
                  src={currentShow.image_url || currentShow.poster_url}
                  alt={currentShow.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 50%)' }} />

                {/* Swipe indicators */}
                {isLiking && (
                  <div style={{ position: 'absolute', top: 20, left: 20, background: 'rgba(34,197,94,0.9)', borderRadius: 8, padding: '6px 14px', border: '2px solid #22c55e' }}>
                    <span style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>YES 👍</span>
                  </div>
                )}
                {isNoping && (
                  <div style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(239,68,68,0.9)', borderRadius: 8, padding: '6px 14px', border: '2px solid #ef4444' }}>
                    <span style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>NOPE 👎</span>
                  </div>
                )}

                {/* Show info */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px' }}>
                  {currentShow.service_id && SERVICE_META[currentShow.service_id] && (
                    <div style={{
                      display: 'inline-block', fontSize: 10, fontWeight: 700, marginBottom: 6,
                      color: SERVICE_META[currentShow.service_id].color,
                    }}>
                      {SERVICE_META[currentShow.service_id].name}
                    </div>
                  )}
                  {currentShow.from_trending && (
                    <div style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, marginBottom: 6, color: '#a78bfa' }}>
                      Trending
                    </div>
                  )}
                  <h2 style={{ fontSize: 22, fontWeight: 900, color: '#fff', margin: 0, lineHeight: 1.2 }}>
                    {currentShow.title}
                  </h2>
                  {currentShow.current_season && (
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: '4px 0 0' }}>
                      Currently S{currentShow.current_season}E{currentShow.current_episode}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ height: 200, background: '#1a1a1a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                <Tv size={36} style={{ color: '#444' }} />
                <p style={{ fontSize: 18, fontWeight: 800, color: '#fff', textAlign: 'center', padding: '0 20px' }}>{currentShow.title}</p>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 16, marginTop: 16, justifyContent: 'center' }}>
            <button
              onClick={() => vote(false)}
              style={{
                width: 64, height: 64, borderRadius: '50%',
                background: 'rgba(239,68,68,0.1)',
                border: '2px solid rgba(239,68,68,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: 24,
                transition: 'transform 0.1s, background 0.1s',
              }}
              onMouseDown={e => e.currentTarget.style.transform = 'scale(0.92)'}
              onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              ❌
            </button>
            <button
              onClick={() => vote(true)}
              style={{
                width: 64, height: 64, borderRadius: '50%',
                background: 'rgba(34,197,94,0.1)',
                border: '2px solid rgba(34,197,94,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: 24,
                transition: 'transform 0.1s, background 0.1s',
              }}
              onMouseDown={e => e.currentTarget.style.transform = 'scale(0.92)'}
              onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              ❤️
            </button>
          </div>

          <p style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.2)', marginTop: 8 }}>
            Swipe right to say yes · Swipe left to skip
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main QMate component ──────────────────────────────────────────────────────

export default function QMate() {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('qmates');
  const [swipeQmateId, setSwipeQmateId] = useState(null);
  const [swipeQmateName, setSwipeQmateName] = useState('');

  const [qmates, setQmates] = useState([]);
  const [queues, setQueues] = useState([]);
  const [activeQueue, setActiveQueue] = useState(null);
  const [queueProgress, setQueueProgress] = useState([]);
  const [inviteInput, setInviteInput] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteStep, setInviteStep] = useState('search');
  const [newQueueTitle, setNewQueueTitle] = useState('');
  const [newQueueMate, setNewQueueMate] = useState('');
  const [queueOpen, setQueueOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [username, setUsername] = useState('');
  const [usernameSet, setUsernameSet] = useState(false);
  const [sending, setSending] = useState(false);

  const [feed, setFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);

  const [ratings, setRatings] = useState({});
  const [ratingQueue, setRatingQueue] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingReview, setRatingReview] = useState('');
  const [savingRating, setSavingRating] = useState(false);

  const [parties, setParties] = useState([]);
  const [partiesLoading, setPartiesLoading] = useState(false);
  const [partyOpen, setPartyOpen] = useState(false);
  const [partyForm, setPartyForm] = useState({ qmate_id: '', show_title: '', show_image: '', tmdb_id: '', scheduled_at: '', note: '' });
  const [partySearch, setPartySearch] = useState('');
  const [partySearchResults, setPartySearchResults] = useState([]);
  const [partySearching, setPartySearching] = useState(false);
  const [creatingParty, setCreatingParty] = useState(false);

  const debounceRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [qmatesRes, queuesRes, meRes] = await Promise.all([
        axios.get(`${API}/qmates`, { headers }),
        axios.get(`${API}/queues`, { headers }),
        axios.get(`${API}/auth/me`, { headers }),
      ]);
      setQmates(qmatesRes.data);
      setQueues(queuesRes.data);
      if (meRes.data.username) { setUsername(meRes.data.username); setUsernameSet(true); }
    } catch (err) {}
  }, [getToken]);

  const fetchFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/activity/feed`, { headers: { Authorization: `Bearer ${token}` } });
      setFeed(res.data.events || []);
    } catch (err) {}
    setFeedLoading(false);
  }, [getToken]);

  const fetchParties = useCallback(async () => {
    setPartiesLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/watch-parties`, { headers: { Authorization: `Bearer ${token}` } });
      setParties(res.data.parties || []);
    } catch (err) {}
    setPartiesLoading(false);
  }, [getToken]);

  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);
  useEffect(() => {
    if (tab === 'activity') fetchFeed();
    if (tab === 'parties') fetchParties();
  }, [tab, fetchFeed, fetchParties]);

  const saveUsername = async () => {
    if (!username.trim() || username.length < 3) { toast.error('Username must be at least 3 characters'); return; }
    try {
      const token = await getToken();
      await axios.post(`${API}/users/username`, { username }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Username set!');
      setUsernameSet(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to set username'); }
  };

  const sendInvite = async () => {
    if (!inviteInput.trim()) return;
    try {
      const token = await getToken();
      await axios.post(`${API}/qmates/invite`, { identifier: inviteInput }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invite sent!');
      setInviteInput(''); setInviteOpen(false); setInviteStep('search');
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'User not found') { setInviteStep('notfound'); }
      else { toast.error(detail || 'Failed to send invite'); }
    }
  };

  const sendEmailInvite = async () => {
    try {
      setSending(true);
      const token = await getToken();
      await axios.post(`${API}/invite/email`, { email: inviteInput }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Email invite sent!');
      setInviteOpen(false); setInviteStep('search'); setInviteInput('');
    } catch (err) { toast.error('Failed to send email invite'); }
    finally { setSending(false); }
  };

  const sendSMSInvite = () => {
    const senderName = user?.firstName || user?.fullName || 'Someone';
    const message = `Hey! ${senderName} invited you to join Qude — the app that tracks all your shows across Netflix, Hulu, Disney+ and more. Sign up free at https://qudetv.com`;
    window.open(`sms:${inviteInput.replace(/\D/g, '')}?body=${encodeURIComponent(message)}`, '_self');
  };

  const acceptInvite = async (qmateId) => {
    try {
      const token = await getToken();
      await axios.post(`${API}/qmates/${qmateId}/accept`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('QMate accepted!'); fetchData();
    } catch (err) { toast.error('Failed to accept'); }
  };

  const removeQmate = async (qmateId) => {
    try {
      const token = await getToken();
      await axios.delete(`${API}/qmates/${qmateId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('QMate removed'); fetchData();
    } catch (err) { toast.error('Failed to remove'); }
  };

  const createQueue = async () => {
    if (!newQueueTitle.trim() || !newQueueMate) return;
    try {
      const token = await getToken();
      await axios.post(`${API}/queues`, { title: newQueueTitle, qmate_id: newQueueMate }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Queue created!');
      setNewQueueTitle(''); setNewQueueMate(''); setQueueOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create queue'); }
  };

  const openQueue = async (queue) => {
    setActiveQueue(queue);
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/progress/${queue.queue_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setQueueProgress(res.data);
    } catch (err) {}
  };

  const searchTMDB = async (q) => {
    const query = q || searchQuery;
    if (!query.trim()) return;
    setSearching(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/tmdb/search`, { params: { q: query }, headers: { Authorization: `Bearer ${token}` } });
      setSearchResults(res.data.results || []);
    } catch (err) { toast.error('Search failed'); }
    setSearching(false);
  };

  const addShowToQueue = async (show) => {
    if (!activeQueue) return;
    try {
      const token = await getToken();
      await axios.post(`${API}/queues/${activeQueue.queue_id}/shows`, {
        show_title: show.title, image_url: show.poster_url || '', tmdb_id: String(show.tmdb_id || ''),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Show added!');
      setSearchResults([]); setSearchQuery(''); openQueue(activeQueue);
    } catch (err) { toast.error('Failed to add show'); }
  };

  const updateProgress = async (queueShowId, season, episode) => {
    try {
      const token = await getToken();
      await axios.post(`${API}/progress`, {
        queue_show_id: queueShowId, season: parseInt(season) || 1, episode: parseInt(episode) || 1, status: 'watching',
      }, { headers: { Authorization: `Bearer ${token}` } });
      openQueue(activeQueue);
    } catch (err) { toast.error('Failed to update progress'); }
  };

  const submitRating = async () => {
    if (!ratingQueue || !ratingValue) return;
    setSavingRating(true);
    try {
      const token = await getToken();
      await axios.post(`${API}/shows/${ratingQueue.show_id}/rate`, { rating: ratingValue, review: ratingReview }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Rated ${ratingValue} ★`);
      setRatings(prev => ({ ...prev, [ratingQueue.show_id]: { my_rating: ratingValue } }));
      setRatingQueue(null); setRatingValue(0); setRatingReview('');
    } catch (err) { toast.error('Failed to save rating'); }
    setSavingRating(false);
  };

  const searchPartyShow = (val) => {
    setPartySearch(val);
    setPartyForm(f => ({ ...f, show_title: val, show_image: '', tmdb_id: '' }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setPartySearchResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setPartySearching(true);
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/tmdb/search`, { params: { q: val }, headers: { Authorization: `Bearer ${token}` } });
        setPartySearchResults(res.data.results?.slice(0, 5) || []);
      } catch (err) {}
      setPartySearching(false);
    }, 350);
  };

  const selectPartyShow = (show) => {
    setPartyForm(f => ({ ...f, show_title: show.title, show_image: show.poster_url || '', tmdb_id: String(show.tmdb_id || '') }));
    setPartySearch(show.title); setPartySearchResults([]);
  };

  const createParty = async () => {
    if (!partyForm.qmate_id || !partyForm.show_title || !partyForm.scheduled_at) { toast.error('Please fill in all fields'); return; }
    setCreatingParty(true);
    try {
      const token = await getToken();
      await axios.post(`${API}/watch-parties`, partyForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Watch party created! Your QMate has been notified.');
      setPartyOpen(false);
      setPartyForm({ qmate_id: '', show_title: '', show_image: '', tmdb_id: '', scheduled_at: '', note: '' });
      setPartySearch(''); fetchParties();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create party'); }
    setCreatingParty(false);
  };

  const cancelParty = async (partyId) => {
    try {
      const token = await getToken();
      await axios.delete(`${API}/watch-parties/${partyId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Watch party cancelled'); fetchParties();
    } catch (err) { toast.error('Failed to cancel'); }
  };

  const isEmail = (val) => /\S+@\S+\.\S+/.test(val);
  const isPhone = (val) => /^[\d\s\-+()]{7,}$/.test(val.trim());
  const acceptedQmates = qmates.filter(q => q.status === 'accepted');
  const pendingQmates = qmates.filter(q => q.status === 'incoming');

  if (!usernameSet) {
    return (
      <div className="max-w-md mx-auto mt-20 space-y-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-violet-500/10 flex items-center justify-center mx-auto">
          <Users className="w-8 h-8 text-violet-400" />
        </div>
        <h1 className="text-2xl font-black" style={{ fontFamily: 'Outfit' }}>Set your username</h1>
        <p className="text-neutral-500 text-sm">Your QMates will find you by your username or email.</p>
        <div className="flex gap-2">
          <Input placeholder="Choose a username" value={username}
            onChange={e => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
            className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-12" />
          <Button onClick={saveUsername} className="bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-xl h-12 px-6">Save</Button>
        </div>
      </div>
    );
  }

  if (activeQueue) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={() => setActiveQueue(null)} className="text-neutral-400 hover:text-white transition-colors text-sm">← Back</button>
          <h1 className="text-2xl font-black" style={{ fontFamily: 'Outfit' }}>{activeQueue.title}</h1>
        </div>
        <div className="p-5 rounded-2xl bg-[#0A0A0A] border border-white/5 space-y-3">
          <p className="text-sm font-semibold text-neutral-400">Add a show</p>
          <div className="flex gap-2">
            <Input placeholder="Search movies and TV shows..." value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchTMDB()}
              className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-10" />
            <Button onClick={() => searchTMDB()} disabled={searching} className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl h-10 px-4">
              <Search className="w-4 h-4" />
            </Button>
          </div>
          {searchResults.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-black/40 divide-y divide-white/5">
              {searchResults.map(result => (
                <button key={result.tmdb_id} onClick={() => addShowToQueue(result)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left">
                  {result.poster_url && <img src={result.poster_url} alt={result.title} className="w-8 h-12 rounded object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{result.title}</p>
                    <p className="text-xs text-neutral-500">{result.media_type === 'movie' ? 'Movie' : 'TV'} {result.year && `· ${result.year}`}</p>
                  </div>
                  <Plus className="w-4 h-4 text-neutral-500" />
                </button>
              ))}
            </div>
          )}
        </div>
        {queueProgress.length === 0 ? (
          <div className="text-center py-12">
            <Tv className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
            <p className="text-neutral-500">No shows yet. Add your first show above!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {queueProgress.map(show => {
              const myS = show.my_progress?.season || 1;
              const myEp = show.my_progress?.episode || 0;
              return (
                <div key={show.queue_show_id} className="p-5 rounded-2xl bg-[#0A0A0A] border border-white/5 space-y-4">
                  <div className="flex items-center gap-3">
                    {show.image_url && <img src={show.image_url} alt={show.show_title} className="w-10 h-14 rounded-lg object-cover flex-shrink-0" />}
                    <div className="flex-1">
                      <p className="font-semibold">{show.show_title}</p>
                      <div className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full mt-1 ${
                        show.drift === 0 ? 'bg-emerald-500/10 text-emerald-400'
                        : show.drift > 0 ? 'bg-amber-500/10 text-amber-400'
                        : 'bg-blue-500/10 text-blue-400'
                      }`}>
                        {show.drift === 0 ? 'In sync' : show.drift > 0 ? `You're ${show.drift} ep ahead` : `You're ${Math.abs(show.drift)} ep behind`}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-xs text-neutral-500">Your progress</p>
                      <div className="flex gap-2">
                        <Input type="number" placeholder="S" defaultValue={myS}
                          className="bg-black/20 border-white/10 text-white rounded-lg h-9 text-xs w-16"
                          id={`s-${show.queue_show_id}`} />
                        <Input type="number" placeholder="EP" defaultValue={myEp}
                          className="bg-black/20 border-white/10 text-white rounded-lg h-9 text-xs w-16"
                          id={`e-${show.queue_show_id}`} />
                        <Button onClick={() => {
                          const s = document.getElementById(`s-${show.queue_show_id}`).value;
                          const e = document.getElementById(`e-${show.queue_show_id}`).value;
                          updateProgress(show.queue_show_id, s, e);
                        }} className="bg-violet-600 hover:bg-violet-700 text-white rounded-lg h-9 px-3 text-xs">
                          Update
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs text-neutral-500">Their progress</p>
                      <p className="text-sm text-neutral-400">
                        {show.mate_progress ? `S${show.mate_progress.season} E${show.mate_progress.episode}` : 'Not started'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Swipe mode — full screen
  if (swipeQmateId) {
    return (
      <div className="max-w-md mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setSwipeQmateId(null)} className="text-neutral-400 hover:text-white transition-colors text-sm">← Back</button>
          <h1 className="text-xl font-black" style={{ fontFamily: 'Outfit' }}>
            What to watch? <span className="text-violet-400">with {swipeQmateName}</span>
          </h1>
        </div>
        <SwipeMatch qmateId={swipeQmateId} qmateName={swipeQmateName} getToken={getToken} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>QMate</h1>
          <p className="text-neutral-500 mt-1">Watch together, stay in sync</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={inviteOpen} onOpenChange={open => { setInviteOpen(open); if (!open) { setInviteStep('search'); setInviteInput(''); } }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold gap-2 hover:opacity-90">
                <UserPlus className="w-4 h-4" /> Add QMate
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bold" style={{ fontFamily: 'Outfit' }}>Add a QMate</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {inviteStep === 'search' && (
                  <>
                    <p className="text-sm text-neutral-400">Enter their username or email address</p>
                    <Input placeholder="username or email" value={inviteInput}
                      onChange={e => setInviteInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && sendInvite()}
                      className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-12" />
                    <Button onClick={sendInvite} className="w-full bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold hover:opacity-90">
                      Send Invite
                    </Button>
                  </>
                )}
                {inviteStep === 'notfound' && (
                  <>
                    <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                      <p className="text-sm text-amber-400">"{inviteInput}" doesn't have a Qude account yet.</p>
                      <p className="text-xs text-neutral-500 mt-1">Invite them to join!</p>
                    </div>
                    {isEmail(inviteInput) && (
                      <Button onClick={sendEmailInvite} disabled={sending} className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full font-semibold gap-2">
                        <Mail className="w-4 h-4" />{sending ? 'Sending...' : 'Send Email Invite'}
                      </Button>
                    )}
                    {isPhone(inviteInput) && (
                      <Button onClick={sendSMSInvite} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-semibold gap-2">
                        <MessageSquare className="w-4 h-4" />Send Text Invite
                      </Button>
                    )}
                    {!isEmail(inviteInput) && !isPhone(inviteInput) && (
                      <div className="space-y-3">
                        <Button onClick={() => { window.location.href = `mailto:${inviteInput}?subject=Join me on Qude&body=Hey! I invited you to join Qude at https://qudetv.com`; }}
                          className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full font-semibold gap-2">
                          <Mail className="w-4 h-4" />Send Email Invite
                        </Button>
                        <Button onClick={() => { window.open(`sms:?body=${encodeURIComponent('Join me on Qude: https://qudetv.com')}`, '_self'); }}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-semibold gap-2">
                          <MessageSquare className="w-4 h-4" />Send Text Invite
                        </Button>
                      </div>
                    )}
                    <button onClick={() => setInviteStep('search')} className="w-full text-xs text-neutral-500 hover:text-white transition-colors">
                      ← Try a different username or email
                    </button>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
          {acceptedQmates.length > 0 && (
            <Dialog open={queueOpen} onOpenChange={setQueueOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-white/10 text-white rounded-full gap-2 hover:bg-white/5">
                  <Plus className="w-4 h-4" /> New Queue
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-bold" style={{ fontFamily: 'Outfit' }}>Create a Queue</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input placeholder="Queue name (e.g. Saturday Night Shows)" value={newQueueTitle}
                    onChange={e => setNewQueueTitle(e.target.value)}
                    className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-12" />
                  <select value={newQueueMate} onChange={e => setNewQueueMate(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-12 px-3 text-sm">
                    <option value="">Select a QMate</option>
                    {acceptedQmates.map(q => <option key={q.qmate_id} value={q.qmate_id}>{q.name || q.username}</option>)}
                  </select>
                  <Button onClick={createQueue} className="w-full bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold hover:opacity-90">
                    Create Queue
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Username badge */}
      <div className="p-4 rounded-xl bg-[#0A0A0A] border border-white/5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
            <span className="text-xs text-violet-400 font-bold">@</span>
          </div>
          <div>
            <p className="text-sm font-medium">Your username: <span className="text-violet-400">@{username}</span></p>
            <p className="text-xs text-neutral-500">Share this so QMates can find you</p>
          </div>
        </div>
        <button onClick={() => navigate(`/profile/${username}`)} className="flex items-center gap-1 text-xs text-neutral-500 hover:text-white transition-colors">
          <ExternalLink className="w-3.5 h-3.5" /> View profile
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-white/5 border border-white/8">
        {[
          { id: 'qmates',  label: 'QMates',        icon: Users },
          { id: 'swipe',   label: 'What to Watch?', icon: Heart },
          { id: 'activity',label: 'Activity',       icon: Activity },
          { id: 'parties', label: 'Watch Parties',  icon: Calendar },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${
              tab === t.id ? 'bg-violet-600 text-white' : 'text-neutral-400 hover:text-white'
            }`}>
            <t.icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* ── Tab: QMates ── */}
      {tab === 'qmates' && (
        <div className="space-y-6">
          {pendingQmates.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xs font-black tracking-widest text-neutral-500 uppercase">Pending Invites ({pendingQmates.length})</h2>
              {pendingQmates.map(q => (
                <div key={q.qmate_id} className="flex items-center gap-3 p-4 rounded-xl bg-violet-500/5 border border-violet-500/20">
                  <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-violet-400">{(q.name || q.username || '?')[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{q.name || q.username}</p>
                    <p className="text-xs text-neutral-500">wants to be your QMate</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => acceptInvite(q.qmate_id)} className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 rounded-full text-xs h-8 px-3">
                      <Check className="w-3 h-3 mr-1" /> Accept
                    </Button>
                    <Button onClick={() => removeQmate(q.qmate_id)} variant="ghost" className="text-neutral-500 hover:text-red-400 rounded-full text-xs h-8 px-3">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-3">
            <h2 className="text-xs font-black tracking-widest text-neutral-500 uppercase">Your QMates ({acceptedQmates.length})</h2>
            {acceptedQmates.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
                <p className="text-neutral-500 text-sm">No QMates yet. Invite someone to start watching together!</p>
              </div>
            ) : (
              acceptedQmates.map(q => (
                <div key={q.qmate_id} className="flex items-center gap-3 p-4 rounded-xl bg-[#0A0A0A] border border-white/5">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {q.picture ? <img src={q.picture} alt={q.name} className="w-full h-full rounded-full object-cover" />
                      : <span className="text-sm font-bold text-neutral-400">{(q.name || q.username || '?')[0].toUpperCase()}</span>}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{q.name || q.username}</p>
                    {q.username && <p className="text-xs text-neutral-500">@{q.username}</p>}
                  </div>
                  {/* Swipe button */}
                  <button
                    onClick={() => { setSwipeQmateId(q.qmate_id); setSwipeQmateName(q.name || q.username); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white transition-all hover:opacity-90 mr-1"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #ec4899)' }}>
                    <Heart className="w-3 h-3" /> Pick
                  </button>
                  <button onClick={() => removeQmate(q.qmate_id)} className="p-2 text-neutral-600 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
          {queues.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xs font-black tracking-widest text-neutral-500 uppercase">Shared Queues ({queues.length})</h2>
              {queues.map(q => (
                <button key={q.queue_id} onClick={() => openQueue(q)}
                  className="w-full flex items-center gap-3 p-4 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-violet-500/30 transition-all text-left">
                  <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                    <Tv className="w-5 h-5 text-violet-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{q.title}</p>
                    <p className="text-xs text-neutral-500">{q.shows?.length || 0} shows</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-neutral-500" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Swipe Match ── */}
      {tab === 'swipe' && (
        <div className="space-y-4">
          {acceptedQmates.length === 0 ? (
            <div className="text-center py-16">
              <Heart className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
              <p className="text-neutral-500 text-sm mb-2">Add a QMate first to start swiping.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-neutral-500">Pick a QMate to find something you both want to watch.</p>
              <div className="space-y-3">
                {acceptedQmates.map(q => (
                  <button key={q.qmate_id}
                    onClick={() => { setSwipeQmateId(q.qmate_id); setSwipeQmateName(q.name || q.username); }}
                    className="w-full flex items-center gap-3 p-4 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-violet-500/30 transition-all text-left">
                    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {q.picture ? <img src={q.picture} alt={q.name} className="w-full h-full rounded-full object-cover" />
                        : <span className="text-sm font-bold text-neutral-400">{(q.name || q.username || '?')[0].toUpperCase()}</span>}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{q.name || q.username}</p>
                      <p className="text-xs text-neutral-500">Tap to start swiping</p>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white"
                      style={{ background: 'linear-gradient(135deg, #7c3aed, #ec4899)' }}>
                      <Heart className="w-3 h-3" /> Pick
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Tab: Activity ── */}
      {tab === 'activity' && (
        <div className="space-y-3">
          {feedLoading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-neutral-500">
              <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
              Loading activity...
            </div>
          ) : feed.length === 0 ? (
            <div className="text-center py-16">
              <Activity className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
              <p className="text-neutral-500 text-sm">
                {acceptedQmates.length === 0 ? 'Add QMates to see their activity here.' : "No activity yet — your QMates haven't watched anything recently."}
              </p>
            </div>
          ) : (
            <>
              {/* Now Watching */}
              {feed.filter(e => e.show_status === 'watching').length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-black tracking-widest text-emerald-400 uppercase flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse inline-block" /> Watching Now
                  </p>
                  {feed.filter(e => e.show_status === 'watching').map(event => {
                    const meta = SERVICE_META[event.service_id];
                    return (
                      <div key={`now-${event.event_id}`}
                        className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20 hover:border-emerald-500/40 transition-all cursor-pointer"
                        onClick={() => event.tmdb_id && navigate(`/show/${event.tmdb_id}`)}>
                        <div className="relative w-10 h-10 flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-white/5 overflow-hidden flex items-center justify-center">
                            {event.mate_picture ? <img src={event.mate_picture} alt={event.mate_name} className="w-full h-full object-cover" />
                              : <span className="text-sm font-bold text-neutral-400">{(event.mate_name || '?')[0].toUpperCase()}</span>}
                          </div>
                          <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#050505]" />
                        </div>
                        <div className="w-10 h-14 rounded-lg bg-white/5 flex-shrink-0 overflow-hidden">
                          {event.show_image ? <img src={event.show_image} alt={event.show_title} className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center"><Tv className="w-4 h-4 text-neutral-600" /></div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white">
                            <span className="font-bold">{event.mate_name}</span>
                            <span className="text-emerald-400"> is watching </span>
                            <span className="font-semibold">{event.show_title}</span>
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {meta && <span className="text-xs text-neutral-500 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} />{meta.name}</span>}
                            {event.current_season && event.current_episode && <span className="text-xs text-neutral-600">S{event.current_season}E{event.current_episode}</span>}
                            <TimeAgo isoString={event.started_at} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Recent activity */}
              {feed.filter(e => e.show_status !== 'watching').length > 0 && (
                <div className="space-y-2">
                  {feed.some(e => e.show_status === 'watching') && (
                    <p className="text-xs font-black tracking-widest text-neutral-500 uppercase">Recent</p>
                  )}
                  {feed.filter(e => e.show_status !== 'watching').map(event => {
                    const meta = SERVICE_META[event.service_id];
                    return (
                      <div key={event.event_id}
                        className="flex items-center gap-3 p-4 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-white/10 transition-all cursor-pointer"
                        onClick={() => event.tmdb_id && navigate(`/show/${event.tmdb_id}`)}>
                        <div className="w-10 h-10 rounded-full bg-white/5 flex-shrink-0 overflow-hidden flex items-center justify-center">
                          {event.mate_picture ? <img src={event.mate_picture} alt={event.mate_name} className="w-full h-full object-cover" />
                            : <span className="text-sm font-bold text-neutral-400">{(event.mate_name || '?')[0].toUpperCase()}</span>}
                        </div>
                        <div className="w-10 h-14 rounded-lg bg-white/5 flex-shrink-0 overflow-hidden">
                          {event.show_image ? <img src={event.show_image} alt={event.show_title} className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center"><Tv className="w-4 h-4 text-neutral-600" /></div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white">
                            <span className="font-bold">{event.mate_name}</span>
                            {event.show_status === 'watched' ? <span className="text-neutral-400"> finished </span> : <span className="text-neutral-400"> watched </span>}
                            <span className="font-semibold">{event.show_title}</span>
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {meta && <span className="text-xs text-neutral-500 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} />{meta.name}</span>}
                            {event.current_season && event.current_episode && <span className="text-xs text-neutral-600">S{event.current_season}E{event.current_episode}</span>}
                            <TimeAgo isoString={event.started_at} />
                          </div>
                        </div>
                        {event.show_status === 'watched' && (
                          <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 flex-shrink-0">Finished</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          {feed.length > 0 && (
            <button onClick={fetchFeed} className="w-full py-3 text-xs text-neutral-500 hover:text-white transition-colors">Refresh</button>
          )}
        </div>
      )}

      {/* ── Tab: Watch Parties ── */}
      {tab === 'parties' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-black tracking-widest text-neutral-500 uppercase">Upcoming Parties</p>
            {acceptedQmates.length > 0 && (
              <Dialog open={partyOpen} onOpenChange={setPartyOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full text-sm gap-2 h-9 px-4 font-semibold hover:opacity-90">
                    <Plus className="w-3.5 h-3.5" /> Schedule Party
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-bold" style={{ fontFamily: 'Outfit' }}>Schedule a Watch Party</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="text-xs text-neutral-500 block mb-1.5">Watch with</label>
                      <select value={partyForm.qmate_id} onChange={e => setPartyForm(f => ({ ...f, qmate_id: e.target.value }))}
                        className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-12 px-3 text-sm">
                        <option value="">Select a QMate</option>
                        {acceptedQmates.map(q => <option key={q.qmate_id} value={q.qmate_id}>{q.name || q.username}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-neutral-500 block mb-1.5">What to watch</label>
                      <div className="relative">
                        <Input placeholder="Search for a show or movie..." value={partySearch} onChange={e => searchPartyShow(e.target.value)}
                          className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-12" />
                        {partySearching && <div className="absolute right-3 top-4 w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />}
                      </div>
                      {partySearchResults.length > 0 && (
                        <div className="mt-1 rounded-xl border border-white/10 bg-black/60 divide-y divide-white/5 overflow-hidden">
                          {partySearchResults.map(r => (
                            <button key={r.tmdb_id} onClick={() => selectPartyShow(r)} className="w-full flex items-center gap-3 p-3 hover:bg-white/5 text-left transition-colors">
                              {r.poster_url && <img src={r.poster_url} alt={r.title} className="w-8 h-11 rounded object-cover flex-shrink-0" />}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{r.title}</p>
                                <p className="text-xs text-neutral-500">{r.year}</p>
                              </div>
                              <Check className="w-4 h-4 text-neutral-600" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-neutral-500 block mb-1.5">When</label>
                      <Input type="datetime-local" value={partyForm.scheduled_at} onChange={e => setPartyForm(f => ({ ...f, scheduled_at: e.target.value }))}
                        className="bg-black/20 border-white/10 text-white rounded-xl h-12 [color-scheme:dark]" />
                    </div>
                    <div>
                      <label className="text-xs text-neutral-500 block mb-1.5">Note (optional)</label>
                      <Input placeholder="e.g. Come over at 8, I'll get snacks" value={partyForm.note} onChange={e => setPartyForm(f => ({ ...f, note: e.target.value }))}
                        className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-12" />
                    </div>
                    <Button onClick={createParty} disabled={creatingParty} className="w-full bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold hover:opacity-90 disabled:opacity-50 gap-2">
                      {creatingParty ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Creating...</> : <><Zap className="w-4 h-4" /> Schedule & Notify QMate</>}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          {partiesLoading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-neutral-500">
              <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : parties.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
              <p className="text-neutral-500 text-sm mb-2">No watch parties scheduled.</p>
              {acceptedQmates.length === 0 ? <p className="text-xs text-neutral-600">Add a QMate first to schedule a party.</p> : <p className="text-xs text-neutral-600">Schedule one with a QMate above!</p>}
            </div>
          ) : (
            <div className="space-y-3">
              {parties.map(party => {
                let timeStr = ''; let isPast = false;
                try {
                  const d = new Date(party.scheduled_at);
                  isPast = d < new Date();
                  timeStr = d.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                } catch {}
                return (
                  <div key={party.party_id} className={`p-4 rounded-2xl border transition-all ${isPast ? 'opacity-60 border-white/5 bg-white/2' : 'border-violet-500/20 bg-violet-500/5'}`}>
                    <div className="flex items-start gap-3">
                      {party.show_image ? <img src={party.show_image} alt={party.show_title} className="w-12 h-16 rounded-xl object-cover flex-shrink-0" />
                        : <div className="w-12 h-16 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0"><Tv className="w-5 h-5 text-neutral-600" /></div>}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-white" style={{ fontFamily: 'Outfit' }}>{party.show_title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {party.other_picture ? <img src={party.other_picture} alt={party.other_name} className="w-full h-full object-cover" />
                              : <span className="text-xs text-neutral-400">{(party.other_name || '?')[0]}</span>}
                          </div>
                          <p className="text-xs text-neutral-400">with {party.other_name}</p>
                          <span className="text-neutral-700">·</span>
                          <span className="text-xs text-neutral-500">{party.is_host ? 'You invited' : 'They invited you'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-2">
                          <Clock className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                          <p className="text-xs font-semibold text-violet-400">{timeStr}</p>
                        </div>
                        {party.note && <p className="text-xs text-neutral-500 mt-1 italic">"{party.note}"</p>}
                      </div>
                      {party.is_host && !isPast && (
                        <button onClick={() => cancelParty(party.party_id)} className="p-1.5 text-neutral-600 hover:text-red-400 transition-colors flex-shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {!isPast && party.tmdb_id && (
                      <button onClick={() => navigate(`/show/${party.tmdb_id}`)}
                        className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                        style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' }}>
                        <Play className="w-4 h-4" /> View Show
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Rating dialog */}
      {ratingQueue && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50 p-4" onClick={() => setRatingQueue(null)}>
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6 w-full max-w-sm space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              {ratingQueue.image_url && <img src={ratingQueue.image_url} alt={ratingQueue.title} className="w-10 h-14 rounded-lg object-cover" />}
              <div>
                <p className="font-bold text-white">{ratingQueue.title}</p>
                <p className="text-xs text-neutral-500">How would you rate it?</p>
              </div>
            </div>
            <div className="flex justify-center">
              <StarRating value={ratingValue} onChange={setRatingValue} />
            </div>
            <Input placeholder="Write a quick review (optional)" value={ratingReview} onChange={e => setRatingReview(e.target.value)}
              className="bg-black/30 border-white/10 text-white placeholder:text-neutral-600 rounded-xl" />
            <div className="flex gap-2">
              <Button onClick={() => setRatingQueue(null)} variant="outline" className="flex-1 border-white/10 text-neutral-400 rounded-full">Skip</Button>
              <Button onClick={submitRating} disabled={!ratingValue || savingRating}
                className="flex-1 bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold disabled:opacity-50">
                {savingRating ? 'Saving...' : 'Save Rating'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
